import './TryoutsList.css';

import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';

import { getFirestore } from 'firebase/firestore';

import { useAuth } from '../../contexts/AuthContext';
import firebase from '../../firebase';
import { fetchGymLogos } from '../../services/gymLogoService';
import {
  fetchTryoutsByUser,
  TryoutListItem,
  updateTryoutAthletePositions,
  updateTryoutAthleteStatuses,
  updateTryoutGymLogo,
  updateTryoutName,
  updateTryoutTeams,
} from '../../services/tryoutService';
import { FormSchema } from '../../types/formSchema';
import { GymLogo } from '../../types/gymLogo';
import { copyTextToClipboard } from '../../utils/clipboard';
import AddablePill from '../AddablePill/AddablePill';
import Button from '../common/Button';
import Card from '../common/Card';
import ConfirmDialog from '../common/ConfirmDialog/ConfirmDialog';
import CopyIcon from '../common/CopyIcon';
import IconButton from '../common/IconButton/IconButton';
import InlineInputWithIntegratedCheckmark
  from '../common/InlineInputWithIntegratedCheckmark/InlineInputWithIntegratedCheckmark';
import PencilIcon from '../common/PencilIcon';

type TryoutPillListField = 'teams' | 'athletePositions' | 'athleteStatuses';

export interface TryoutsListProps {
  /** Increment when tryouts may have changed (e.g. after creating one). */
  refreshKey?: number;
  /** Called when the coach taps "Edit Tryout Form" on a tryout card. */
  onEditForm?: (tryoutId: string, tryoutName: string, formName: string, schema: FormSchema | null) => void;
  /** Renders on the right of the “My Tryouts” title (same row). */
  headerRight?: React.ReactNode;
}

const IS_DEBUG = window.location.hostname === 'localhost';

const TryoutsList: React.FC<TryoutsListProps> = ({
  refreshKey = 0,
  onEditForm,
  headerRight,
}) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [tryouts, setTryouts] = useState<TryoutListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [editingNameId, setEditingNameId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');
  const [savingName, setSavingName] = useState(false);

  const [listDeletePending, setListDeletePending] = useState<{
    field: TryoutPillListField;
    tryoutId: string;
    index: number;
  } | null>(null);
  const [savingTryoutLists, setSavingTryoutLists] = useState(false);
  const [copiedCodeId, setCopiedCodeId] = useState<string | null>(null);
  const copyFeedbackTimeoutRef = useRef<number | null>(null);
  const [copiedPasswordId, setCopiedPasswordId] = useState<string | null>(null);
  const copyPasswordTimeoutRef = useRef<number | null>(null);

  const [gymLogos, setGymLogos] = useState<GymLogo[]>([]);
  const [loadingLogos, setLoadingLogos] = useState(false);
  const [savingLogo, setSavingLogo] = useState<string | null>(null);
  const [logoMenu, setLogoMenu] = useState<{
    tryoutId: string;
    top: number;
    right: number;
  } | null>(null);
  const logoAnchorRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  /** Single memoized value so the effect deps array stays one entry (avoids Fast Refresh errors when deps were previously only `[currentUser]`). */
  const loadSignal = useMemo(
    () => ({
      uid: currentUser?.uid ?? null,
      refreshKey,
    }),
    [currentUser?.uid, refreshKey],
  );

  useEffect(() => {
    const db = getFirestore(firebase);

    async function loadTryouts(): Promise<void> {
      if (!loadSignal.uid) {
        return;
      }
      setLoading(true);
      setError('');
      try {
        const items = await fetchTryoutsByUser(db, loadSignal.uid);
        setTryouts(items);
      } catch (err: unknown) {
        setError('Failed to load tryouts');
        console.error(err);
      } finally {
        setLoading(false);
      }
    }

    void loadTryouts();
  }, [loadSignal]);

  useEffect(() => {
    const db = getFirestore(firebase);

    async function loadGymLogos(): Promise<void> {
      if (!currentUser?.uid) {
        return;
      }
      setLoadingLogos(true);
      try {
        const logos = await fetchGymLogos(db, currentUser.uid);
        setGymLogos(logos);
      } catch (err: unknown) {
        console.error('Failed to load gym logos:', err);
      } finally {
        setLoadingLogos(false);
      }
    }

    void loadGymLogos();
  }, [currentUser?.uid]);

  useEffect(
    () => () => {
      if (copyFeedbackTimeoutRef.current != null) {
        window.clearTimeout(copyFeedbackTimeoutRef.current);
      }
      if (copyPasswordTimeoutRef.current != null) {
        window.clearTimeout(copyPasswordTimeoutRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      const target = event.target as Element;
      if (
        logoMenu
        && !target.closest('.tryouts-list-logo-selector-wrapper')
        && !target.closest('.tryouts-list-logo-dropdown')
      ) {
        setLogoMenu(null);
      }
    };

    if (logoMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [logoMenu]);

  useEffect(() => {
    const tryoutId = logoMenu?.tryoutId;
    if (!tryoutId) {
      return;
    }
    const syncPosition = (): void => {
      const anchor = logoAnchorRefs.current.get(tryoutId);
      if (!anchor) {
        return;
      }
      const rect = anchor.getBoundingClientRect();
      setLogoMenu((prev) => {
        if (!prev || prev.tryoutId !== tryoutId) {
          return prev;
        }
        return {
          ...prev,
          top: rect.bottom + 4,
          right: window.innerWidth - rect.right,
        };
      });
    };

    window.addEventListener('scroll', syncPosition, true);
    window.addEventListener('resize', syncPosition);
    return () => {
      window.removeEventListener('scroll', syncPosition, true);
      window.removeEventListener('resize', syncPosition);
    };
  }, [logoMenu?.tryoutId]);

  const handleCopyJoinCode = (tryoutId: string, code: string): void => {
    const joinUrl = `${window.location.origin}/join?code=${encodeURIComponent(code)}`;
    void copyTextToClipboard(joinUrl).then(
      (didCopy) => {
        if (!didCopy) {
          return;
        }
        if (copyFeedbackTimeoutRef.current != null) {
          window.clearTimeout(copyFeedbackTimeoutRef.current);
        }
        setCopiedCodeId(tryoutId);
        copyFeedbackTimeoutRef.current = window.setTimeout(() => {
          setCopiedCodeId(null);
          copyFeedbackTimeoutRef.current = null;
        }, 2500);
      },
      () => {
        /* clipboard denied — user can still select the code */
      },
    );
  };

  const handleCopyPassword = (tryoutId: string, pw: string): void => {
    void copyTextToClipboard(pw).then((didCopy) => {
      if (!didCopy) return;
      if (copyPasswordTimeoutRef.current != null) {
        window.clearTimeout(copyPasswordTimeoutRef.current);
      }
      setCopiedPasswordId(tryoutId);
      copyPasswordTimeoutRef.current = window.setTimeout(() => {
        setCopiedPasswordId(null);
        copyPasswordTimeoutRef.current = null;
      }, 2500);
    });
  };

  const beginEditName = (tryout: TryoutListItem): void => {
    setEditingNameId(tryout.id);
    setDraftName(tryout.name);
  };

  const cancelEditName = (): void => {
    setEditingNameId(null);
    setDraftName('');
  };

  const saveEditName = async (tryoutId: string): Promise<void> => {
    const trimmed = draftName.trim();
    if (!trimmed) {
      setError('Tryout name cannot be empty');
      return;
    }
    const db = getFirestore(firebase);
    setSavingName(true);
    setError('');
    try {
      await updateTryoutName(db, tryoutId, trimmed);
      setTryouts((prev) =>
        prev.map((t) => (t.id === tryoutId ? { ...t, name: trimmed } : t)),
      );
      cancelEditName();
    } catch (err: unknown) {
      setError('Failed to update name');
      console.error(err);
    } finally {
      setSavingName(false);
    }
  };

  const persistTryoutPillList = async (
    tryoutId: string,
    field: TryoutPillListField,
    next: string[],
  ): Promise<void> => {
    const db = getFirestore(firebase);
    if (field === 'teams') {
      await updateTryoutTeams(db, tryoutId, next);
    } else if (field === 'athletePositions') {
      await updateTryoutAthletePositions(db, tryoutId, next);
    } else {
      await updateTryoutAthleteStatuses(db, tryoutId, next);
    }
    setTryouts((prev) =>
      prev.map((t) => (t.id === tryoutId ? { ...t, [field]: next } : t)),
    );
  };

  const listFieldError = (field: TryoutPillListField, action: 'add' | 'remove'): string => {
    if (field === 'teams') {
      return action === 'add' ? 'Failed to add team' : 'Failed to remove team';
    }
    if (field === 'athletePositions') {
      return action === 'add'
        ? 'Failed to add athlete position'
        : 'Failed to remove athlete position';
    }
    return action === 'add'
      ? 'Failed to add athlete status'
      : 'Failed to remove athlete status';
  };

  const handleAddPillListItem = async (
    tryoutId: string,
    field: TryoutPillListField,
    name: string,
  ): Promise<void> => {
    const tryout = tryouts.find((t) => t.id === tryoutId);
    if (!tryout) {
      return;
    }
    const next = [...tryout[field], name];
    setSavingTryoutLists(true);
    setError('');
    try {
      await persistTryoutPillList(tryoutId, field, next);
    } catch (err: unknown) {
      setError(listFieldError(field, 'add'));
      console.error(err);
    } finally {
      setSavingTryoutLists(false);
    }
  };

  const confirmRemovePillListItem = async (): Promise<void> => {
    if (!listDeletePending) {
      return;
    }
    const { field, tryoutId, index } = listDeletePending;
    const tryout = tryouts.find((t) => t.id === tryoutId);
    if (!tryout) {
      setListDeletePending(null);
      return;
    }
    const next = tryout[field].filter((_, i) => i !== index);
    setSavingTryoutLists(true);
    setError('');
    setListDeletePending(null);
    try {
      await persistTryoutPillList(tryoutId, field, next);
    } catch (err: unknown) {
      setError(listFieldError(field, 'remove'));
      console.error(err);
    } finally {
      setSavingTryoutLists(false);
    }
  };

  const pendingListDeleteLabel =
    listDeletePending != null
      ? tryouts.find((t) => t.id === listDeletePending.tryoutId)?.[
          listDeletePending.field
        ][listDeletePending.index] ?? ''
      : '';

  const listDeleteDialogTitle =
    listDeletePending?.field === 'teams'
      ? 'Remove team?'
      : listDeletePending?.field === 'athletePositions'
        ? 'Remove athlete position?'
        : listDeletePending != null
          ? 'Remove athlete status?'
          : 'Remove?';

  const listDeleteDialogMessage =
    pendingListDeleteLabel
      ? `Are you sure you want to delete “${pendingListDeleteLabel}”?`
      : listDeletePending?.field === 'teams'
        ? 'Are you sure you want to delete this team?'
        : listDeletePending?.field === 'athletePositions'
          ? 'Are you sure you want to delete this athlete position?'
          : 'Are you sure you want to delete this athlete status?';

  const handleSelectGymLogo = async (tryoutId: string, logoId: string | null): Promise<void> => {
    const db = getFirestore(firebase);
    setSavingLogo(tryoutId);
    setError('');
    setLogoMenu(null);
    try {
      const url =
        logoId === null ? null : gymLogos.find((l) => l.id === logoId)?.imgUrl ?? null;
      await updateTryoutGymLogo(db, tryoutId, logoId, url);
      setTryouts((prev) =>
        prev.map((t) =>
          t.id === tryoutId
            ? {
                ...t,
                gymLogoId: logoId ?? undefined,
                gymLogoUrl: logoId === null ? undefined : url ?? undefined,
              }
            : t,
        ),
      );
    } catch (err: unknown) {
      setError('Failed to update gym logo');
      console.error(err);
    } finally {
      setSavingLogo(null);
    }
  };

  const toggleLogoDropdown = (tryoutId: string): void => {
    setLogoMenu((prev) => {
      if (prev?.tryoutId === tryoutId) {
        return null;
      }
      const anchor = logoAnchorRefs.current.get(tryoutId);
      if (!anchor) {
        return null;
      }
      const rect = anchor.getBoundingClientRect();
      return {
        tryoutId,
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
      };
    });
  };

  return (
    <div className="tryouts-list">
      {headerRight != null ? (
        <div className="tryouts-list-header-row">
          <h2 className="tryouts-list-title">My Tryouts</h2>
          <div className="tryouts-list-header-actions">{headerRight}</div>
        </div>
      ) : (
        <h2 className="tryouts-list-title">My Tryouts</h2>
      )}
      {loading ? (
        <p className="tryouts-list-status">Loading...</p>
      ) : null}
      {error ? <div className="tryouts-list-error">{error}</div> : null}
      {!loading && tryouts.length === 0 ? (
        <p className="tryouts-list-status">No tryouts found.</p>
      ) : null}
      <div className="tryouts-list-items">
        {tryouts.map((tryout) => (
          <Card key={tryout.id} className="tryouts-list-card">
            <div className="tryouts-list-card-inner">
              <div className="tryouts-list-card-top">
                <div className="tryouts-list-card-left">
                  <div className="tryouts-list-name-row">
                    {editingNameId === tryout.id ? (
                      <InlineInputWithIntegratedCheckmark
                        value={draftName}
                        onChange={setDraftName}
                        onSave={() => void saveEditName(tryout.id)}
                        onCancel={cancelEditName}
                        saving={savingName}
                        placeholder="Tryout name"
                        inputAriaLabel="Tryout name"
                        commitAriaLabel="Save tryout name"
                      />
                    ) : (
                      <>
                        <h3 className="tryouts-list-card-title">
                          {tryout.name.trim() || 'Untitled tryout'}
                        </h3>
                        <IconButton
                          aria-label="Edit tryout name"
                          onClick={() => beginEditName(tryout)}
                        >
                          <PencilIcon />
                        </IconButton>
                      </>
                    )}
                  </div>
                  <div className="tryouts-list-meta-block">
                    <span className="tryouts-list-meta-label">Created</span>
                    <span className="tryouts-list-meta-value">
                      {tryout.createdAt?.toDate().toLocaleString() ?? '—'}
                    </span>
                  </div>
                  <div className="tryouts-list-code-block">
                    <span
                      className="tryouts-list-code-label"
                      aria-live="polite"
                    >
                      {copiedCodeId === tryout.id
                        ? 'Join Link - copied!'
                        : 'Join Code'}
                    </span>
                    <div
                      className="tryouts-list-code-chip"
                      title="Code shown here; copy button copies full join URL"
                    >
                      <span className="tryouts-list-code-value">{tryout.code}</span>
                      <button
                        type="button"
                        className="tryouts-list-code-copy"
                        onClick={() => handleCopyJoinCode(tryout.id, tryout.code)}
                        title="Copy join link"
                        aria-label="Copy join link to clipboard"
                      >
                        <CopyIcon className="tryouts-list-code-copy-icon" />
                      </button>
                    </div>
                  </div>
                  {tryout.password ? (
                    <div className="tryouts-list-password-block">
                      <span
                        className="tryouts-list-password-label"
                        aria-live="polite"
                      >
                        {copiedPasswordId === tryout.id
                          ? 'Password - copied!'
                          : 'Join Password'}
                      </span>
                      <div className="tryouts-list-password-chip">
                        <span className="tryouts-list-password-value">
                          {tryout.password}
                        </span>
                        <button
                          type="button"
                          className="tryouts-list-code-copy"
                          onClick={() => handleCopyPassword(tryout.id, tryout.password!)}
                          title="Copy password"
                          aria-label="Copy password to clipboard"
                        >
                          <CopyIcon className="tryouts-list-code-copy-icon" />
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
                <div className="tryouts-list-card-right" aria-label="Tryout counts">
                  {/* TODO: Add athlete and coach counts when we do a way without N+1 query */}
                  {/* <div className="tryouts-list-stat-pill">
                    <span className="tryouts-list-stat-pill-label">Athletes</span>
                    <span className="tryouts-list-stat-pill-value">X</span>
                  </div> */}
                  {/* <div className="tryouts-list-stat-pill">
                    <span className="tryouts-list-stat-pill-label">Coaches</span>
                    <span className="tryouts-list-stat-pill-value">X</span>
                  </div> */}
                  <div
                    className="tryouts-list-logo-selector-wrapper"
                    ref={(el) => {
                      if (el) {
                        logoAnchorRefs.current.set(tryout.id, el);
                      } else {
                        logoAnchorRefs.current.delete(tryout.id);
                      }
                    }}
                  >
                    <button
                      type="button"
                      className="tryouts-list-logo-button"
                      onClick={() => toggleLogoDropdown(tryout.id)}
                      disabled={savingLogo === tryout.id || loadingLogos}
                      aria-label="Select gym logo"
                      aria-expanded={logoMenu?.tryoutId === tryout.id}
                    >
                      {tryout.gymLogoId && gymLogos.find((l) => l.id === tryout.gymLogoId) ? (
                        <img
                          src={gymLogos.find((l) => l.id === tryout.gymLogoId)?.imgUrl}
                          alt="Gym logo"
                          className="tryouts-list-logo-image"
                          loading="lazy"
                          decoding="async"
                        />
                      ) : (
                        <span className="tryouts-list-logo-placeholder">GYM LOGO</span>
                      )}
                    </button>
                  </div>
                </div>
              </div>
              <div className="tryouts-list-teams-section tryouts-list-pill-groups">
                <AddablePill
                  sectionLabel="Teams"
                  items={tryout.teams}
                  disabled={savingTryoutLists}
                  onAdd={(name) => handleAddPillListItem(tryout.id, 'teams', name)}
                  onRemoveItem={(index) =>
                    setListDeletePending({ field: 'teams', tryoutId: tryout.id, index })
                  }
                  inputPlaceholder="Team name"
                  addButtonAriaLabel="Add team"
                  commitButtonAriaLabel="Add team"
                  removeButtonAriaLabel={(label) => `Remove team ${label}`}
                />
                <AddablePill
                  sectionLabel="Athlete Positions"
                  items={tryout.athletePositions}
                  inputPlaceholder="Position name"
                  addButtonAriaLabel="Add athlete position"
                  commitButtonAriaLabel="Add athlete position"
                  removeButtonAriaLabel={(label) => `Remove athlete position ${label}`}
                  disabled={savingTryoutLists}
                  onAdd={(name) =>
                    handleAddPillListItem(tryout.id, 'athletePositions', name)
                  }
                  onRemoveItem={(index) =>
                    setListDeletePending({
                      field: 'athletePositions',
                      tryoutId: tryout.id,
                      index,
                    })
                  }
                />
                <AddablePill
                  sectionLabel="Athlete Status"
                  items={tryout.athleteStatuses}
                  inputPlaceholder="Status name"
                  addButtonAriaLabel="Add athlete status"
                  commitButtonAriaLabel="Add athlete status"
                  removeButtonAriaLabel={(label) => `Remove athlete status ${label}`}
                  disabled={savingTryoutLists}
                  onAdd={(name) =>
                    handleAddPillListItem(tryout.id, 'athleteStatuses', name)
                  }
                  onRemoveItem={(index) =>
                    setListDeletePending({
                      field: 'athleteStatuses',
                      tryoutId: tryout.id,
                      index,
                    })
                  }
                />
              </div>
              {onEditForm ? (
                <div className="tryouts-list-card-actions">
                  <div className="tryouts-list-form-line">
                    <span className="tryouts-list-form-label">Tryout Form:</span>
                    <span
                      className={`tryouts-list-form-name ${!tryout.formName?.trim() ? 'tryouts-list-form-name-muted' : ''}`}
                      title={
                        tryout.formName?.trim()
                          ? tryout.formName.trim()
                          : 'No evaluation form name yet'
                      }
                    >
                      {tryout.formName?.trim()
                        ? tryout.formName.trim()
                        : 'No form'}
                    </span>
                  </div>
                  <Button
                    type="button"
                    variant="secondary"
                    compact
                    onClick={() =>
                      onEditForm(tryout.id, tryout.name, tryout.formName ?? '', tryout.formSchema ?? null)
                    }
                  >
                    Edit Tryout Form
                  </Button>
                </div>
              ) : null}
              {IS_DEBUG ? (
                <div className="tryouts-list-debug-export">
                  <Button
                    type="button"
                    variant="secondary"
                    compact
                    onClick={() =>
                      navigate(`/admin/export/${tryout.id}`, {
                        state: { tryoutName: tryout.name },
                      })
                    }
                  >
                    Export Data (debug)
                  </Button>
                </div>
              ) : null}
            </div>
          </Card>
        ))}
      </div>

      {logoMenu
        ? createPortal(
            <div
              className="tryouts-list-logo-dropdown"
              style={{ top: logoMenu.top, right: logoMenu.right }}
            >
              <button
                type="button"
                className="tryouts-list-logo-dropdown-item"
                onClick={() => void handleSelectGymLogo(logoMenu.tryoutId, null)}
              >
                <span className="tryouts-list-logo-dropdown-empty">No logo</span>
              </button>
              {gymLogos.map((logo) => (
                <button
                  key={logo.id}
                  type="button"
                  className="tryouts-list-logo-dropdown-item"
                  onClick={() => void handleSelectGymLogo(logoMenu.tryoutId, logo.id)}
                >
                  <img
                    src={logo.imgUrl}
                    alt={logo.name}
                    className="tryouts-list-logo-dropdown-thumb"
                    loading="lazy"
                    decoding="async"
                  />
                  <span className="tryouts-list-logo-dropdown-name">{logo.name}</span>
                </button>
              ))}
            </div>,
            document.body,
          )
        : null}

      <ConfirmDialog
        open={listDeletePending != null}
        title={listDeleteDialogTitle}
        message={listDeleteDialogMessage}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={() => void confirmRemovePillListItem()}
        onCancel={() => setListDeletePending(null)}
      />
    </div>
  );
};

export default React.memo(TryoutsList);
